/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
    type CreateCheckboxConsentsAndDeclarationsMutation,
    type CreateCheckboxConsentsAndDeclarationsMutationVariables,
    CreateCheckboxConsentsAndDeclarationsDocument,
} from '../../../api/mutations/createCheckboxConsentsAndDeclarations';
import {
    type CreateMarketingConsentsAndDeclarationsMutation,
    type CreateMarketingConsentsAndDeclarationsMutationVariables,
    CreateMarketingConsentsAndDeclarationsDocument,
} from '../../../api/mutations/createMarketingConsentsAndDeclarations';
import {
    type CreateTextConsentsAndDeclarationsMutation,
    type CreateTextConsentsAndDeclarationsMutationVariables,
    CreateTextConsentsAndDeclarationsDocument,
} from '../../../api/mutations/createTextConsentsAndDeclarations';
import {
    type TextConsentsAndDeclarationsSettings,
    type CheckboxConsentsAndDeclarationsSettings,
    type MarketingConsentsAndDeclarationsSettings,
    type ConditionSettings,
    ConsentsAndDeclarationsType,
} from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import prepareSubmissionConditionSettings from '../ConsentsAndDeclarationsDetailsPage/prepareSubmissionConditionSettings';
import useConsentsAndDeclarationsValidator from '../ConsentsAndDeclarationsDetailsPage/useConsentsAndDeclarationsValidator';
import AddConsentsAndDeclarationsPage from './AddConsentsAndDeclarationsPage';

type cndSettings =
    | TextConsentsAndDeclarationsSettings
    | CheckboxConsentsAndDeclarationsSettings
    | MarketingConsentsAndDeclarationsSettings;

type FormValues = cndSettings & {
    conditionSettings?: ConditionSettings[];
    moduleId?: string;
    consentType: ConsentsAndDeclarationsType;
};

const AddConsentsAndDeclarations = () => {
    const { t } = useTranslation(['consentsAndDeclarations']);
    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();
    const { state } = useLocation();
    const validate = useConsentsAndDeclarationsValidator(false);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { moduleId, consentType, conditionSettings: conditionSettingsField, ...settings } = values;

            const conditionSettings = prepareSubmissionConditionSettings(conditionSettingsField);

            setLoading(true);
            message.loading({
                content: t('consentsAndDeclarations:messages.creationSubmitting'),
                duration: 0,
                key: 'primary',
            });
            // call mutation to create new consent and declaration
            switch (consentType) {
                case ConsentsAndDeclarationsType.Text: {
                    const textCndSettings = settings as TextConsentsAndDeclarationsSettings;

                    await apolloClient
                        .mutate<
                            CreateTextConsentsAndDeclarationsMutation,
                            CreateTextConsentsAndDeclarationsMutationVariables
                        >({
                            mutation: CreateTextConsentsAndDeclarationsDocument,
                            variables: {
                                moduleId,
                                settings: textCndSettings,
                                conditionSettings,
                                eventId: state?.eventId,
                            },
                        })
                        .finally(() => {
                            // clean up
                            message.destroy('primary');
                            setLoading(false);
                        });

                    break;
                }

                case ConsentsAndDeclarationsType.Checkbox: {
                    const checkboxCndSettings = settings as CheckboxConsentsAndDeclarationsSettings;

                    await apolloClient
                        .mutate<
                            CreateCheckboxConsentsAndDeclarationsMutation,
                            CreateCheckboxConsentsAndDeclarationsMutationVariables
                        >({
                            mutation: CreateCheckboxConsentsAndDeclarationsDocument,
                            variables: {
                                moduleId,
                                settings: checkboxCndSettings,
                                conditionSettings,
                                eventId: state?.eventId,
                            },
                        })
                        .finally(() => {
                            // clean up
                            message.destroy('primary');
                            setLoading(false);
                        });

                    break;
                }

                case ConsentsAndDeclarationsType.Marketing: {
                    const marketingCndSettings = settings as MarketingConsentsAndDeclarationsSettings;

                    await apolloClient
                        .mutate<
                            CreateMarketingConsentsAndDeclarationsMutation,
                            CreateMarketingConsentsAndDeclarationsMutationVariables
                        >({
                            mutation: CreateMarketingConsentsAndDeclarationsDocument,
                            variables: {
                                moduleId,
                                settings: marketingCndSettings,
                                conditionSettings,
                                eventId: state?.eventId,
                            },
                        })
                        .finally(() => {
                            // clean up
                            message.destroy('primary');
                            setLoading(false);
                        });

                    break;
                }

                default:
                    throw new Error('Consent and declaration type not supported');
            }

            // show successful message
            message.success({
                content: t('consentsAndDeclarations:messages.creationSuccessful'),
                key: 'primary',
            });
            setLoading(false);
            navigate(-1);
        },
        [setLoading, t, navigate, apolloClient, state?.eventId]
    );

    // only the base properties
    const initialValues = useMemo(
        (): FormValues => ({
            displayName: '',
            moduleId: null,
            consentType: null,
            title: { defaultValue: '', overrides: [] },
            orderNumber: null,
            isActive: true,
            description: { defaultValue: '', overrides: [] },
            purpose: undefined,
            dataField: null,
            conditionSettings: [],
        }),
        []
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="consentsAndDeclarationsForm" htmlType="submit" type="primary">
                    {t('consentsAndDeclarations:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate(-1)}
            title={t('consentsAndDeclarations:addTitle')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="consentsAndDeclarationsForm"
                        name="consentsAndDeclarationsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <AddConsentsAndDeclarationsPage initialValues={initialValues} />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddConsentsAndDeclarations;
